import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';

import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import globalTranslations from '../../../../i18n/globalTranslations';
import { normalizeBooleanField } from '../../../../util/checkoutFormUtils';
import CheckoutBillingForm from '../CheckoutBillingForm';
import CheckoutDeliveryForm from '../CheckoutDeliveryForm';
import RecipientDeliveryDraftForm from '../RecipientDeliveryDraftForm';
import SelectField from '../../../form/SelectField';
import {
  createEmptyManualDeliveryDraft,
  setDraftValueFromFieldId,
} from '../manualDeliveryDraftValidation';

const DELIVERY_TAB_ADDRESS_BOOK = 'addressBook';
const DELIVERY_TAB_CREATE = 'create';

const messages = defineMessages({
  addressBookSelectionRequired: {
    id: 'checkout.recipientAddressBookSelectionRequired',
    defaultMessage: 'Please choose a delivery address.',
  },
});

const RecipientCheckoutModal = ({
  isOpen,
  toggle,
  mode,
  billingForm,
  deliveryForm,
  deliveryAddressDropdownField,
  deliveryAddressDropdownFormField,
  deliveryDropdownOptions,
  onCommitDeliveryAddressDropdown,
  onDeliveryModalConfirm,
  onPrepareManualDeliveryForSave,
  onOrderUpdate,
  billingTitle,
  deliveryTitle,
  loading,
  accountStore,
  checkoutStore,
  intl,
}) => {
  const [activeDeliveryTab, setActiveDeliveryTab] = useState(
    DELIVERY_TAB_ADDRESS_BOOK
  );
  const [createDeliveryTitleIsFresh, setCreateDeliveryTitleIsFresh] =
    useState(true);
  const [manualDeliveryDraft, setManualDeliveryDraft] = useState(() =>
    createEmptyManualDeliveryDraft()
  );
  const [manualDeliveryDraftErrors, setManualDeliveryDraftErrors] = useState(
    {}
  );
  const [addressBookDropdownError, setAddressBookDropdownError] = useState('');
  /** null = follow order; boolean = "Save for later" checked (defer ONE_TIME until Save). */
  const [saveToAddressBookIntent, setSaveToAddressBookIntent] = useState(null);

  const resetManualDeliveryDraft = useCallback(() => {
    setManualDeliveryDraft(createEmptyManualDeliveryDraft());
    setManualDeliveryDraftErrors({});
  }, []);

  const showAddressBookTab = useMemo(
    () =>
      Boolean(
        deliveryAddressDropdownField &&
          (deliveryAddressDropdownField.options?.length ?? 0) > 0 &&
          deliveryAddressDropdownFormField &&
          typeof onCommitDeliveryAddressDropdown === 'function'
      ),
    [
      deliveryAddressDropdownField,
      // `options` is often mutated in place on MST; fingerprint so this updates when the list changes.
      (deliveryAddressDropdownField?.options || [])
        .map((o) => `${o.value}:${o.label}`)
        .join('|'),
      deliveryAddressDropdownFormField,
      onCommitDeliveryAddressDropdown,
    ]
  );

  const showCreateTab = Boolean(accountStore?.isUserAllowedAddressModification);

  const deliveryDropdownAriaLabel = intl.formatMessage({
    id: 'recurringOrder.deliveryAddressSentence',
    defaultMessage: 'Delivery address',
  });

  const deliveryModalOpenRef = useRef(false);

  useEffect(() => {
    if (!addressBookDropdownError) return;
    const v = deliveryAddressDropdownFormField?.value;
    if (v !== '' && v != null && String(v).trim() !== '') {
      setAddressBookDropdownError('');
    }
  }, [deliveryAddressDropdownFormField?.value, addressBookDropdownError]);

  useEffect(() => {
    if (!isOpen || mode !== 'delivery') {
      deliveryModalOpenRef.current = false;
      if (!isOpen) {
        setAddressBookDropdownError('');
        setSaveToAddressBookIntent(null);
      }
      return;
    }

    if (deliveryAddressDropdownField && deliveryAddressDropdownFormField) {
      const v = deliveryAddressDropdownField.value;
      deliveryAddressDropdownFormField.setValue(
        v == null || v === '' ? '' : String(v)
      );
    }

    const justOpened = !deliveryModalOpenRef.current;
    deliveryModalOpenRef.current = true;

    if (!justOpened) {
      return;
    }

    const order = checkoutStore?.order;
    const isOneTime = order
      ? normalizeBooleanField(
          order.getCustomerField(FormFieldId.ONE_TIME_DELIVERY_ADDRESS).field
            ?.value
        )
      : false;

    let nextTab = DELIVERY_TAB_CREATE;
    let titleFresh = true;

    if (isOneTime && showCreateTab) {
      nextTab = DELIVERY_TAB_CREATE;
      titleFresh = false;
    } else if (showAddressBookTab) {
      nextTab = DELIVERY_TAB_ADDRESS_BOOK;
    } else if (showCreateTab) {
      nextTab = DELIVERY_TAB_CREATE;
      titleFresh = true;
    }

    setActiveDeliveryTab(nextTab);
    setCreateDeliveryTitleIsFresh(titleFresh);
    setSaveToAddressBookIntent(null);
    if (nextTab === DELIVERY_TAB_CREATE && !isOneTime) {
      resetManualDeliveryDraft();
    }
  }, [
    isOpen,
    mode,
    checkoutStore,
    showAddressBookTab,
    showCreateTab,
    deliveryAddressDropdownField,
    deliveryAddressDropdownFormField,
    resetManualDeliveryDraft,
  ]);

  const selectDeliveryTab = useCallback(
    (tabId) => {
      setAddressBookDropdownError('');
      setSaveToAddressBookIntent(null);
      if (
        tabId === DELIVERY_TAB_ADDRESS_BOOK &&
        activeDeliveryTab === DELIVERY_TAB_CREATE &&
        deliveryAddressDropdownField &&
        deliveryAddressDropdownFormField
      ) {
        const v = deliveryAddressDropdownField.value;
        deliveryAddressDropdownFormField.setValue(
          v == null || v === '' ? '' : String(v)
        );
      }
      if (
        tabId === DELIVERY_TAB_CREATE &&
        activeDeliveryTab === DELIVERY_TAB_ADDRESS_BOOK
      ) {
        const order = checkoutStore?.order;
        const isOneTime = order
          ? normalizeBooleanField(
              order.getCustomerField(FormFieldId.ONE_TIME_DELIVERY_ADDRESS)
                .field?.value
            )
          : false;
        setCreateDeliveryTitleIsFresh(!isOneTime);
        resetManualDeliveryDraft();
      }
      setActiveDeliveryTab(tabId);
    },
    [
      activeDeliveryTab,
      checkoutStore,
      deliveryAddressDropdownField,
      deliveryAddressDropdownFormField,
      resetManualDeliveryDraft,
    ]
  );

  const orderForOneTime = checkoutStore?.order;
  const isOneTimeDeliveryAddress = orderForOneTime
    ? normalizeBooleanField(
        orderForOneTime.getCustomerField(FormFieldId.ONE_TIME_DELIVERY_ADDRESS)
          .field?.value
      )
    : false;

  const handleManualDraftFieldChange = useCallback((fieldId, value) => {
    setManualDeliveryDraft((d) => setDraftValueFromFieldId(d, fieldId, value));
    setManualDeliveryDraftErrors((errs) => {
      if (!errs[fieldId]) return errs;
      const next = { ...errs };
      delete next[fieldId];
      return next;
    });
  }, []);

  const showDeliveryTabs = showAddressBookTab && showCreateTab;

  const effectiveShowDeliveryForm = Boolean(
    showCreateTab &&
      deliveryForm &&
      (showDeliveryTabs
        ? activeDeliveryTab === DELIVERY_TAB_CREATE
        : !showAddressBookTab)
  );

  const useManualDeliveryDraftPath = Boolean(
    effectiveShowDeliveryForm && !isOneTimeDeliveryAddress
  );

  const handleUpdate = ({ withoutTimer }) => {
    onOrderUpdate({ withoutTimer });
  };

  const handleBillingDone = () => {
    onOrderUpdate({ withoutTimer: true });
    toggle();
  };

  const handleDeliveryDone = () => {
    if (
      showAddressBookTab &&
      activeDeliveryTab === DELIVERY_TAB_ADDRESS_BOOK &&
      typeof onCommitDeliveryAddressDropdown === 'function'
    ) {
      const committed = onCommitDeliveryAddressDropdown();
      if (committed === false) {
        setAddressBookDropdownError(
          intl.formatMessage(messages.addressBookSelectionRequired)
        );
        return;
      }
      setAddressBookDropdownError('');
      toggle();
      return;
    }

    if (effectiveShowDeliveryForm) {
      if (typeof onPrepareManualDeliveryForSave === 'function') {
        onPrepareManualDeliveryForSave();
      }
      if (typeof onDeliveryModalConfirm === 'function') {
        const result = onDeliveryModalConfirm({
          manualDeliveryDraft: useManualDeliveryDraftPath
            ? manualDeliveryDraft
            : undefined,
          saveToAddressBook: useManualDeliveryDraftPath
            ? manualDeliveryDraft.saveToAddressBook
            : saveToAddressBookIntent != null
            ? saveToAddressBookIntent
            : undefined,
          deferredSaveToAddressBookChecked:
            !useManualDeliveryDraftPath && saveToAddressBookIntent != null
              ? saveToAddressBookIntent
              : undefined,
        });
        if (result != null && typeof result.then === 'function') {
          result
            .then(() => toggle())
            .catch((e) => {
              if (e && e.fieldErrors) {
                setManualDeliveryDraftErrors(e.fieldErrors);
              }
            });
          return;
        }
      }
    }
    onOrderUpdate({ withoutTimer: true });
    toggle();
  };

  const headerTitle = mode === 'billing' ? billingTitle : deliveryTitle;

  const addressBookPane = showAddressBookTab ? (
    <div className="RecipientCheckoutModal__dropdown-block">
      <SelectField
        field={deliveryAddressDropdownFormField}
        fieldName={FormFieldId.DELIVERY_ADDRESS_DROPDOWN}
        formName="checkout"
        aria-label={deliveryDropdownAriaLabel}
        options={deliveryDropdownOptions}
        required={deliveryAddressDropdownField.required}
        customError={addressBookDropdownError || undefined}
      />
    </div>
  ) : null;

  const createSectionTitle = createDeliveryTitleIsFresh ? (
    <FormattedMessage
      id="checkout.recipientDeliveryFormSectionTitleCreate"
      defaultMessage="Create a new address"
    />
  ) : (
    <FormattedMessage
      id="checkout.recipientDeliveryFormSectionTitle"
      defaultMessage="Modify address"
    />
  );

  const createAddressFormEl =
    showCreateTab && deliveryForm ? (
      useManualDeliveryDraftPath ? (
        <RecipientDeliveryDraftForm
          deliveryForm={deliveryForm}
          draft={manualDeliveryDraft}
          onDraftFieldChange={handleManualDraftFieldChange}
          onDeliveryIsCompanyChange={(checked) =>
            setManualDeliveryDraft((d) => ({
              ...d,
              deliveryIsCompany: checked,
            }))
          }
          onSaveToAddressBookChange={(checked) =>
            setManualDeliveryDraft((d) => ({
              ...d,
              saveToAddressBook: checked,
            }))
          }
          fieldErrors={manualDeliveryDraftErrors}
          sectionTitle={createSectionTitle}
          showSaveToAddressBookSwitch={
            Boolean(accountStore?.isUserAllowedAddressModification) &&
            activeDeliveryTab === DELIVERY_TAB_CREATE
          }
        />
      ) : (
        <CheckoutDeliveryForm
          onOrderUpdate={handleUpdate}
          deliveryForm={deliveryForm}
          hideDeliveryAddressDropdown={showAddressBookTab}
          sectionTitle={createSectionTitle}
          showSaveToAddressBookSwitch={
            Boolean(accountStore?.isUserAllowedAddressModification) &&
            activeDeliveryTab === DELIVERY_TAB_CREATE
          }
          deferSaveToAddressBookSwitch
          saveToAddressBookSwitchChecked={saveToAddressBookIntent}
          onSaveToAddressBookSwitchIntentChange={setSaveToAddressBookIntent}
        />
      )
    ) : null;

  const renderDeliveryModeBody = () => {
    if (showDeliveryTabs) {
      return (
        <>
          <Nav tabs className="RecipientCheckoutModal__nav">
            <NavItem>
              <NavLink
                href="#"
                className={classNames({
                  active: activeDeliveryTab === DELIVERY_TAB_ADDRESS_BOOK,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  selectDeliveryTab(DELIVERY_TAB_ADDRESS_BOOK);
                }}
              >
                <FormattedMessage
                  id="checkout.recipientDeliveryModalTabAddressBook"
                  defaultMessage="Address book"
                />
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                className={classNames({
                  active: activeDeliveryTab === DELIVERY_TAB_CREATE,
                })}
                onClick={(e) => {
                  e.preventDefault();
                  selectDeliveryTab(DELIVERY_TAB_CREATE);
                }}
              >
                <FormattedMessage
                  id="checkout.recipientDeliveryModalTabCreateAddress"
                  defaultMessage="Create new address"
                />
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeDeliveryTab}>
            <TabPane tabId={DELIVERY_TAB_ADDRESS_BOOK}>
              {addressBookPane}
            </TabPane>
            <TabPane tabId={DELIVERY_TAB_CREATE}>
              {activeDeliveryTab === DELIVERY_TAB_CREATE
                ? createAddressFormEl
                : null}
            </TabPane>
          </TabContent>
        </>
      );
    }
    if (showAddressBookTab) {
      return addressBookPane;
    }
    return createAddressFormEl;
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="RecipientCheckoutModal"
      backdrop="static"
      size="lg"
    >
      <ModalHeader toggle={toggle} className="RecipientCheckoutModal__header">
        {headerTitle}
      </ModalHeader>
      <ModalBody className="RecipientCheckoutModal__body">
        {mode === 'billing' && billingForm && (
          <div className="RecipientCheckoutModal__form-wrap">
            <CheckoutBillingForm
              onOrderUpdate={handleUpdate}
              billingForm={billingForm}
              hideSeparateDeliveryToggle
            />
          </div>
        )}
        {mode === 'delivery' && (
          <div className="RecipientCheckoutModal__form-wrap">
            {renderDeliveryModeBody()}
          </div>
        )}
      </ModalBody>
      {mode === 'billing' && (
        <ModalFooter className="RecipientCheckoutModal__footer">
          <Button
            color="secondary"
            type="button"
            className="RecipientCheckoutModal__save-button"
            onClick={handleBillingDone}
          >
            <FormattedMessage {...globalTranslations.saveTitle} />
          </Button>
        </ModalFooter>
      )}
      {mode === 'delivery' && (
        <ModalFooter className="RecipientCheckoutModal__footer">
          <Button
            color="secondary"
            type="button"
            className="RecipientCheckoutModal__save-button"
            disabled={Boolean(loading && effectiveShowDeliveryForm)}
            onClick={handleDeliveryDone}
          >
            <FormattedMessage {...globalTranslations.saveTitle} />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

RecipientCheckoutModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['billing', 'delivery']).isRequired,
  billingForm: PropTypes.object,
  deliveryForm: PropTypes.object,
  deliveryAddressDropdownField: PropTypes.object,
  deliveryAddressDropdownFormField: PropTypes.object,
  deliveryDropdownOptions: PropTypes.array,
  onCommitDeliveryAddressDropdown: PropTypes.func,
  onDeliveryModalConfirm: PropTypes.func,
  onPrepareManualDeliveryForSave: PropTypes.func,
  onOrderUpdate: PropTypes.func.isRequired,
  billingTitle: PropTypes.node.isRequired,
  deliveryTitle: PropTypes.node.isRequired,
  loading: PropTypes.bool,
  accountStore: modelOf(AccountStore),
  checkoutStore: modelOf(CheckoutStore),
  intl: intlShape.isRequired,
};

RecipientCheckoutModal.defaultProps = {
  billingForm: null,
  deliveryForm: null,
  deliveryAddressDropdownField: null,
  deliveryAddressDropdownFormField: null,
  deliveryDropdownOptions: [],
  onCommitDeliveryAddressDropdown: undefined,
  onDeliveryModalConfirm: undefined,
  onPrepareManualDeliveryForSave: undefined,
  loading: false,
  accountStore: undefined,
  checkoutStore: undefined,
};

export default inject(
  'accountStore',
  'checkoutStore'
)(injectIntl(observer(RecipientCheckoutModal)));
